import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import CreateEventModal from "./Events/CreateEventModal";
import "./AdminLayout.css";
import "./TopmateBookings/TopmateBookings.css";

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [time, setTime] = useState(new Date());
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminName, setAdminName] = useState("Admin");

  const isTopmateActive = location.pathname.startsWith("/admin/topmatebookings");
  const [topmateExpanded, setTopmateExpanded] = useState(isTopmateActive);

  const isEventsActive = location.pathname.startsWith("/admin/events");
  const [eventsExpanded, setEventsExpanded] = useState(isEventsActive);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith("/admin/topmatebookings")) {
      setTopmateExpanded(true);
    }
    if (location.pathname.startsWith("/admin/events")) {
      setEventsExpanded(true);
    }
  }, [location.pathname]);

  // Live Clock & Admin Info
  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    const storedUser = localStorage.getItem("adminUser");
    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        if (userObj.name) setAdminName(userObj.name);
      } catch (e) {}
    }

    return () => clearInterval(timer);
  }, []);

  // Determine Greeting based on hour
  const getGreeting = () => {
    const hour = time.getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  // Formatted date values
  const currentDay = time.toLocaleDateString("en-US", { weekday: "long" });
  const currentDate = time.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
  const liveTimeStr = time.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true
  });

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    sessionStorage.setItem("adminLogoutNotify", "true");
    navigate("/admin");
    window.location.reload();
  };

  const navItems = [
    { label: "Dashboard", icon: "📊", path: "/admin" },
    { label: "Contact Messages", icon: "📩", path: "/admin/contact-messages" },
    { label: "Feedback Messages", icon: "⭐", path: "/admin/feedback-messages" },
    {
      label: "Topmate Bookings",
      icon: "📅",
      path: "/admin/topmatebookings",
      isParent: true,
      menuType: "topmate"
    },
    {
      label: "Events",
      icon: "🎟️",
      path: "/admin/events",
      isParent: true,
      menuType: "events"
    },
    { label: "Project Likes", icon: "❤️", path: "/admin/project-likes" },
    { label: "Portfolio Analytics", icon: "📈", path: "/admin/portfolio-analytics" },
    { label: "Project Sharing Analytics", icon: "🔗", path: "/admin/project-sharing-analytics" },
    { label: "Blog Sharing Analytics", icon: "📰", path: "/admin/blog-sharing-analytics" },
    { label: "Event Sharing Analytics", icon: "📊", path: "/admin/event-sharing-analytics" },
    { label: "Settings", icon: "⚙️", path: "/admin/settings" }
  ];

  const topmateSubItems = [
    { label: "Overview", icon: "📊", path: "/admin/topmatebookings" },
    { label: "Recent Bookings", icon: "📋", path: "/admin/topmatebookings/recent" },
    { label: "Digital Purchases", icon: "🛍️", path: "/admin/topmatebookings/purchases" },
    { label: "Analytics", icon: "📈", path: "/admin/topmatebookings/analytics" },
    { label: "Email Logs", icon: "✉️", path: "/admin/topmatebookings/emaillogs" },
    { label: "Webhook Logs", icon: "⚡", path: "/admin/topmatebookings/webhooklogs" }
  ];

  const eventsSubItems = [
    { label: "Events Overview", icon: "📊", path: "/admin/events" },
    { label: "All Events", icon: "📋", path: "/admin/events/all" },
    { label: "Create Event", icon: "➕", action: "create_modal" },
    { label: "Registrations", icon: "👥", path: "/admin/events/registrations" },
    { label: "Event Sharing Analytics", icon: "🔗", path: "/admin/event-sharing-analytics" }
  ];

  return (
    <div className="admin-layout-container">
      {/* MOBILE DRAWER OVERLAY */}
      {mobileOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* SIDEBAR (Desktop Permanent, Mobile Drawer) */}
      <aside className={`admin-sidebar ${mobileOpen ? "open" : ""}`}>
        <div className="admin-sidebar-brand">
          <div className="brand-logo">🚀</div>
          <div className="brand-text">
            <h2>Praveen Admin</h2>
            <span>SaaS Control Panel</span>
          </div>
        </div>

        <nav className="admin-sidebar-menu">
          {navItems.map((item) => {
            if (item.isParent) {
              const isEvents = item.menuType === "events";
              const active = isEvents ? isEventsActive : isTopmateActive;
              const isExpanded = isEvents ? eventsExpanded : topmateExpanded;
              const subItemsList = isEvents ? eventsSubItems : topmateSubItems;

              return (
                <div key={item.path}>
                  <div
                    className={`sidebar-nav-item ${active ? "active" : ""}`}
                    style={{ cursor: "pointer", justifyContent: "space-between" }}
                    onClick={() => {
                      if (isEvents) {
                        if (!isEventsActive) {
                          navigate(item.path);
                          setEventsExpanded(true);
                        } else {
                          setEventsExpanded(!eventsExpanded);
                        }
                      } else {
                        if (!isTopmateActive) {
                          navigate(item.path);
                          setTopmateExpanded(true);
                        } else {
                          setTopmateExpanded(!topmateExpanded);
                        }
                      }
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <span className="nav-icon">{item.icon}</span>
                      <span className="nav-label">{item.label}</span>
                    </div>
                    <span style={{ fontSize: "10px", color: "#64748b" }}>
                      {isExpanded ? "▼" : "▶"}
                    </span>
                  </div>

                  {isExpanded && (
                    <div className="sidebar-submenu">
                      {subItemsList.map((sub, sIdx) => {
                        if (sub.action === "create_modal") {
                          return (
                            <div
                              key="create-event-modal-trigger"
                              className="sidebar-sub-item"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setIsCreateModalOpen(true);
                                setMobileOpen(false);
                              }}
                            >
                              <span>{sub.icon}</span>
                              <span>{sub.label}</span>
                            </div>
                          );
                        }

                        const isSubActive =
                          location.pathname === sub.path ||
                          (sub.path === "/admin/topmatebookings/recent" &&
                            location.pathname === "/admin/topmatebookings/recent-bookings");
                        return (
                          <Link
                            key={sub.path || sIdx}
                            to={sub.path}
                            className={`sidebar-sub-item ${isSubActive ? "active" : ""}`}
                            onClick={() => setMobileOpen(false)}
                          >
                            <span>{sub.icon}</span>
                            <span>{sub.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive =
              location.pathname === item.path ||
              (item.path === "/admin/project-sharing-analytics" && location.pathname === "/admin/share-analytics");
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-nav-item ${isActive ? "active" : ""}`}
                onClick={() => setMobileOpen(false)}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer">
          <button className="sidebar-logout-btn" onClick={handleLogout}>
            <span className="nav-icon">🚪</span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <div className="admin-main-wrapper">
        {/* HEADER BAR */}
        <header className="admin-dashboard-header">
          <div className="header-left">
            <button
              className="hamburger-btn"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle Sidebar Menu"
            >
              ☰
            </button>

            <div className="header-greeting">
              <h1>
                {getGreeting()}, <span className="admin-highlight">{adminName}</span> 👋
              </h1>
              <p className="header-date-info">
                {currentDay}, {currentDate}
              </p>
            </div>
          </div>

          <div className="header-right">
            <div className="live-clock-badge">
              <span className="clock-icon">🕒</span>
              <span className="clock-time">{liveTimeStr}</span>
            </div>

            <div className="admin-profile-pill">
              <div className="profile-avatar">PK</div>
              <span className="profile-name">Praveen</span>
            </div>
          </div>
        </header>

        {/* CONTENT VIEW */}
        <main className="admin-content-body">{children}</main>
      </div>

      {/* CREATE EVENT MODAL */}
      <CreateEventModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onEventCreated={(newEvent) => {
          navigate("/admin/events/all");
        }}
      />
    </div>
  );
};

export default AdminLayout;
